import { blue as darkBlue, red, green } from '@mui/material/colors';
import {
  THEME_PRIMARY_ACCENT_COLOR,
  THEME_SECONDARY_ACCENT_COLOR,
  ERROR_DARK_COLOR,
} from '../../constants/Values';

export default {
  palette: {
    primary: {
      accent: THEME_PRIMARY_ACCENT_COLOR,
      light: darkBlue[700],
      main: THEME_PRIMARY_ACCENT_COLOR,
      dark: darkBlue[900],
      contrastText: '#fff',
    },
    secondary: {
      accent: THEME_SECONDARY_ACCENT_COLOR,
      light: green[300],
      main: green.A200,
      dark: green[700],
      contrastText: '#fff',
    },
    error: {
      light: red.A200,
      main: '#ac1614', // $danger (styles/global/_variables.scss); was red.A400 (#ff1744) — a mismatch
      dark: ERROR_DARK_COLOR,
    },
  },
  status: {
    danger: 'orange',
  },
  typography: {
    button: {
      fontWeight: 400,
      // MUI's default button line-height (1.75) + textTransform:none (set in
      // MuiButton.styleOverrides.root) makes icon+label buttons render the label
      // visually low against the startIcon. 1.5 lines them up.
      lineHeight: 1.5,
    },
  },
  // MUI v5+ moved component style overrides from `overrides` to
  // `components.<Name>.styleOverrides`, and the Button variant slot
  // `raisedPrimary` (v1-beta) is now `containedPrimary`.
  components: {
    // MUI v5+ defaults fields to size="medium" (taller than the old v1-beta
    // compact default, which had no size prop). Restore the dense look globally
    // via defaultProps so every field/select/picker is small without per-site props.
    MuiTextField: { defaultProps: { size: 'small' } },
    MuiFormControl: { defaultProps: { size: 'small' } },
    MuiSelect: { defaultProps: { size: 'small' } },
    // MUI 7 renders FormControlLabel's label as body1 (16px); the old console
    // used 14px for every checkbox / radio / switch label. Restore it globally.
    MuiFormControlLabel: {
      styleOverrides: {
        label: { fontSize: '0.875rem' },
      },
    },
    MuiButton: {
      // Old buttons were flat (box-shadow:none); disableElevation keeps contained
      // buttons flat globally, matching the old look without per-site overrides.
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 8,
          // textTransform:none was forced via _button.scss !important; theme owns it now.
          textTransform: 'none',
        },
        // MUI 7 gives TEXT-variant buttons roughly half the horizontal padding of
        // contained/outlined. The genuinely-flat buttons that remain (Cancel /
        // dismiss) still need comfortable padding, so restore the contained-scale
        // side padding per text size. (containedPrimary background removed — the
        // primary palette already provides #025c91.)
        textSizeSmall: { padding: '4px 10px' },
        textSizeMedium: { padding: '6px 16px' },
        textSizeLarge: { padding: '8px 22px' },
        // A contained button rendered as an anchor (component={Link}) inherits
        // bootstrap's `a:hover { color }` (specificity 0,1,1), which overrides
        // MUI's white contained label on hover. Re-assert the variant's own text
        // colour on hover at the slot level — `.css-hash:hover` (0,2,0) beats
        // `a:hover`, and `--variant-containedColor` keeps it correct for every
        // contained colour. No-op for contained <button>s (already white).
        contained: {
          '&:hover': { color: 'var(--variant-containedColor)' },
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          margin: '15px 5px',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        body: { fontSize: '0.94rem' },
        // MUI 7 defaults table-head cells to text.primary (near-black); the old
        // console used the muted secondary grey so headers recede and row data
        // reads as the emphasis. Restore it.
        head: { fontSize: '0.94rem', color: 'rgba(0, 0, 0, 0.54)' },
        root: { padding: '0 12px' },
        // The blanket root padding above overrides MUI 7's built-in padding
        // variants (they live inside the styled root, and theme styleOverrides.root
        // wins over them). Re-assert padding:0 for `padding="none"` cells so they
        // stay genuinely flush; the paddingNone slot applies after root, so it wins.
        paddingNone: { padding: 0 },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        // `min-height` is ignored on `display: table-row`; `height` is the
        // property that acts as a MINIMUM for table rows (the row still grows if
        // its content is taller, but never shrinks below this). Gives rows a
        // 48px floor now that the cell vertical padding is 0.
        root: { height: '48px' },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        asterisk: {
          color: 'red',
        },
      },
    },
    // Dialog content can host wide tables/forms; clip horizontal overflow
    // instead of letting it push the dialog wider than its paper.
    // (was a blanket `*[class*='MuiDialogContent']` rule in custom.scss)
    MuiDialogContent: {
      styleOverrides: {
        root: { overflowX: 'hidden' },
      },
    },
    // Old console set every native placeholder to 16px via a blanket
    // `::placeholder` selector in ui/_input.scss; restore it scoped to MUI
    // inputs only.
    MuiInputBase: {
      styleOverrides: {
        input: {
          '&::placeholder': { fontSize: 16 },
        },
      },
    },
  },
};
